import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StickyNote from '../StickyNote';
import Toolbar from '../Toolbar';
import SideChatPane from '../SideChatPane';
import Toast from '../Toast';
import PersonaSelectionModal from '../PersonaSelectionModal';
import ConnectionLabel from '../ConnectionLabel';
import LogoIcon from '../LogoIcon';
import HelpModal from '../HelpModal';
import SaveStatusIndicator from '../SaveStatusIndicator';
import CanvasNameDisplay from '../CanvasNameDisplay';
import CanvasNameModal from '../CanvasNameModal';
import AlertModal from '../AlertModal';
import MobileTreeView from '../MobileTreeView/MobileTreeView';
import MobileToolbar from '../MobileToolbar/MobileToolbar';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import { StickyNote as StickyNoteType } from '../../types';
import { getAppVersion } from '../../config/api';
import { useFeatureAccess } from '../../hooks/useQuota';
import { useAuth } from '../../hooks/useAuth';


import { needsCanvasNaming, handleCanvasNaming } from '../../utils/canvasNamingUtils';
import { useAutoSave } from '../../hooks/useAutoSave';
// CACHE DEBUG OVERLAY VIEWS - DISABLED
// import SubscriptionCacheDebug from '../SubscriptionCacheDebug';
// import QuotaCacheDebug from '../QuotaCacheDebug';
import './Whiteboard.css';

const Whiteboard: React.FC = () => {
  const {
    state: { notes, scale, offset, isSelectionMode, selectionBox, selectedNoteIds, canvasId },
    deselectAll,
    selectNote,
    startSelectionBox,
    updateSelectionBox,
    endSelectionBox,
    pan,
    zoom,
    addNote,
    showToast,
  } = useWhiteboardStore();

  const { user } = useAuth();
  const { canCreateThreadStarter, canCreateTotalNote } = useFeatureAccess();
  
  // Check if any note is currently creating a branch
  const isAnyNoteCreatingBranch = notes.some(note => note.isCreatingBranch);
  
  // Initialize auto-save when canvas is ready
  useAutoSave();

  const boardRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  
  // Touch state for panning and zooming
  const [touchState, setTouchState] = useState({
    isPanning: false,
    isZooming: false,
    lastTouchPos: { x: 0, y: 0 },
    initialDistance: 0,
    initialScale: 1,
    initialOffset: { x: 0, y: 0 }
  });
  const [sidePaneOpen, setSidePaneOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<StickyNoteType | null>(null);
  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [lastCreatedBranchId, setLastCreatedBranchId] = useState<string | null>(null);
  const [showMergeToast, setShowMergeToast] = useState(false);
  const [mergeToastMessage, setMergeToastMessage] = useState('');
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [personaModalNoteId, setPersonaModalNoteId] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showCanvasNameModal, setShowCanvasNameModal] = useState(false);
  const [pendingKeyboardNote, setPendingKeyboardNote] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);

  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  // Mobile note click handlers
  const handleNoteClick = (note: StickyNoteType) => {
    selectNote(note.id, false);
    setSelectedNote(note);
    setSidePaneOpen(true);
  };

  const handleNoteDoubleClick = (note: StickyNoteType) => {
    selectNote(note.id, false);
    setSelectedNote(note);
    setSidePaneOpen(true);
    // For mobile, we'll open the chat pane directly instead of topic edit modal
    // The topic edit functionality is available through the side chat pane
  };



  const handleMobileDelete = (note: StickyNoteType) => {
    // Delete the note using the store's deleteNote function
    useWhiteboardStore.getState().deleteNote(note.id);
  };

  const handleMobileColorChange = (note: StickyNoteType, color: string) => {
    // Update the note color using the store's updateNote function
    useWhiteboardStore.getState().updateNote(note.id, { color });
  };

  // Update selected note when selectedNoteIds changes
  useEffect(() => {
    if (selectedNoteIds.length === 1) {
      const note = notes.find(n => n.id === selectedNoteIds[0]);
      setSelectedNote(note || null);
      // Open side pane when a note is selected
      if (note) {
        setSidePaneOpen(true);
        
        // Check if this note needs persona selection
        // Don't show persona modal if canvas naming modal is still open
        if (note.needsPersonaSelection && !showCanvasNameModal) {
          setShowPersonaModal(true);
          setPersonaModalNoteId(note.id);
          
          // Dispatch event to notify other components that persona modal is opening
          window.dispatchEvent(new CustomEvent('persona-modal-opened'));
          
          // Hide loading state immediately when persona modal opens
          setShowLoadingState(false);
        }
      }
    } else if (selectedNoteIds.length === 0) {
      // Clear selected note if no notes are selected
      setSelectedNote(null);
    }
  }, [selectedNoteIds, notes, showCanvasNameModal]);

  // Canvas name modal handlers
  const handleCanvasNameSave = async (canvasName: string): Promise<void> => {
    if (!user || !pendingKeyboardNote) return;

    setShowLoadingState(true);

    // Fallback: Hide loading state after 3 seconds if something goes wrong
    const loadingTimeout = setTimeout(() => {
      setShowLoadingState(false);
    }, 3000);

    // Return a Promise that resolves when the entire process is complete
    return new Promise((resolve, reject) => {
      // Handle canvas saving first, then check quotas before creating note
      handleCanvasNaming(
        canvasName,
        user,
        showToast,
        showAlert,
        async (canvasId) => {
          // Canvas saved successfully, now check quotas before creating note
          const effectiveCanvasId = canvasId;

          try {
            // Pre-check quotas before creating the note
            const canCreateStarter = await canCreateThreadStarter();
            const canCreateTotal = await canCreateTotalNote();
            
            if (!canCreateStarter) {
              showToast('You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.', 'error');
              clearTimeout(loadingTimeout);
              setPendingKeyboardNote(false);
              setShowLoadingState(false);
              reject(new Error('Thread starter limit reached'));
              return;
            }
            
            if (!canCreateTotal) {
              showToast('You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.', 'error');
              clearTimeout(loadingTimeout);
              setPendingKeyboardNote(false);
              setShowLoadingState(false);
              reject(new Error('Total notes limit reached'));
              return;
            }

            // Quotas are good, create the note
            addNote();

            // Note: Quota will be incremented when persona is selected, not here
            // This prevents quota increment if user cancels persona selection

            // Clear pending state and hide loading
            clearTimeout(loadingTimeout);
            setPendingKeyboardNote(false);
            setShowLoadingState(false);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve();
          } catch (error) {
            console.error('Error handling quotas after canvas naming (keyboard):', error);
            // Fallback: create note anyway
            addNote();
            clearTimeout(loadingTimeout);
            setPendingKeyboardNote(false);
            setShowLoadingState(false);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve(); // Still resolve since note was created
          }
        },
        (error) => {
          // Canvas save failed, clear loading state
          clearTimeout(loadingTimeout);
          setPendingKeyboardNote(false);
          setShowLoadingState(false);
          showToast(`Failed to save canvas: ${error}`, 'error');
          reject(new Error(error));
        }
      );
    });
  };

  const handleCanvasNameCancel = () => {
    setShowCanvasNameModal(false);
    setPendingKeyboardNote(false);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key to deselect all
      if (e.key === 'Escape') {
        deselectAll();
      }

      // Ctrl+N to create a new note
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        
        // Check quota if user is authenticated
        if (user) {
          // Check if canvas needs naming (no canvas ID)
          if (needsCanvasNaming(canvasId)) {
            // Show canvas name modal first
            setPendingKeyboardNote(true);
            setShowCanvasNameModal(true);
            return;
          }

          // Use existing canvas ID for saved canvases, or default for unsaved ones
          const effectiveCanvasId = canvasId || 'default';
          
          // Pre-check quotas before creating the note
          Promise.all([
            canCreateThreadStarter(),
            canCreateTotalNote()
          ]).then(([canCreateStarter, canCreateTotal]) => {
            if (!canCreateStarter) {
              showToast('You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.', 'error');
              return;
            }
            
            if (!canCreateTotal) {
              showToast('You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.', 'error');
              return;
            }
            
            // Quotas are good, create the note
            addNote();
            
            // Note: Quota will be incremented when persona is selected, not here
            // This prevents quota increment if user cancels persona selection
          }).catch(error => {
            console.error('Error checking quota for note creation (Ctrl+N):', error);
            // Fallback: allow note creation even if quota check fails
            addNote();
          });
        } else {
          // No user, allow note creation
          addNote();
        }
      }

      // Ctrl+Shift+C to toggle side pane
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'c') {
        e.preventDefault();
        setSidePaneOpen(prev => !prev);
      }

      // F1 or Ctrl+Shift+H to open help modal
      if (e.key === 'F1' || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'h')) {
        e.preventDefault();
        setShowHelpModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [deselectAll]);

  // Handle wheel events for zooming
  useEffect(() => {
    const boardElement = boardRef.current;
    if (!boardElement) return;

    boardElement.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      boardElement.removeEventListener('wheel', handleWheel);
    };
  }, [scale, zoom]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle mouse down on the board itself, not on notes
    if (e.target !== boardRef.current) return;

    if (isSelectionMode) {
      // Start selection box
      const rect = boardRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / scale - offset.x;
        const y = (e.clientY - rect.top) / scale - offset.y;
        startSelectionBox(x, y);
      }
    } else {
      // Start panning
      setIsDragging(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isSelectionMode && selectionBox) {
      // Update selection box
      const rect = boardRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / scale - offset.x;
        const y = (e.clientY - rect.top) / scale - offset.y;
        updateSelectionBox(x, y);
      }
    } else if (isDragging) {
      // Pan the board
      const dx = e.clientX - lastMousePos.x;
      const dy = e.clientY - lastMousePos.y;
      pan(dx / scale, dy / scale);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    if (isSelectionMode && selectionBox) {
      endSelectionBox();
    } else if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleWheel = (e: WheelEvent) => {
    // Check if any modal is open by looking for modal overlays, backdrops, or dialogs in the DOM
    const modalSelectors = [
      '[class*="modal-overlay"]',
      '[class*="modal-backdrop"]', 
      '[class*="-overlay"]',
      '[class*="-backdrop"]',
      '[role="dialog"]'
    ];
    
    const hasOpenModal = modalSelectors.some(selector => {
      const elements = document.querySelectorAll(selector);
      return Array.from(elements).some(element => {
        const computedStyle = window.getComputedStyle(element);
        return computedStyle.display !== 'none' && 
               computedStyle.visibility !== 'hidden' && 
               computedStyle.opacity !== '0';
      });
    });

    // If a modal is open, don't prevent default scroll behavior
    if (hasOpenModal) {
      return;
    }

    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    zoom(scale + delta);
  };

  const handleBoardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle clicks on the board itself, not on notes
    if (e.target === boardRef.current) {
      deselectAll();
      // Close the side pane when clicking on the canvas
      setSidePaneOpen(false);
    }
  };

  // Touch event handlers
  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1: React.Touch, touch2: React.Touch) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Only handle touches on the board itself, not on notes
    if (e.target !== boardRef.current) return;

    e.preventDefault();
    const touches = e.touches;

    if (touches.length === 1) {
      // Single touch - start panning or selection
      const touch = touches[0];
      const rect = boardRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (isSelectionMode) {
        // Start selection box
        const x = (touch.clientX - rect.left) / scale - offset.x;
        const y = (touch.clientY - rect.top) / scale - offset.y;
        startSelectionBox(x, y);
      } else {
        // Start panning
        setTouchState(prev => ({
          ...prev,
          isPanning: true,
          lastTouchPos: { x: touch.clientX, y: touch.clientY }
        }));
      }
    } else if (touches.length === 2) {
      // Two touches - start pinch-to-zoom
      const distance = getDistance(touches[0], touches[1]);
      const center = getCenter(touches[0], touches[1]);
      
      setTouchState(prev => ({
        ...prev,
        isZooming: true,
        isPanning: false,
        initialDistance: distance,
        initialScale: scale,
        initialOffset: { x: offset.x, y: offset.y },
        lastTouchPos: center
      }));
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touches = e.touches;

    if (touches.length === 1 && touchState.isPanning) {
      // Single touch panning
      const touch = touches[0];
      const dx = touch.clientX - touchState.lastTouchPos.x;
      const dy = touch.clientY - touchState.lastTouchPos.y;
      
      pan(dx / scale, dy / scale);
      
      setTouchState(prev => ({
        ...prev,
        lastTouchPos: { x: touch.clientX, y: touch.clientY }
      }));
    } else if (touches.length === 1 && isSelectionMode && selectionBox) {
      // Single touch selection box update
      const touch = touches[0];
      const rect = boardRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (touch.clientX - rect.left) / scale - offset.x;
        const y = (touch.clientY - rect.top) / scale - offset.y;
        updateSelectionBox(x, y);
      }
    } else if (touches.length === 2 && touchState.isZooming) {
      // Two touches - pinch-to-zoom
      const distance = getDistance(touches[0], touches[1]);
      const center = getCenter(touches[0], touches[1]);
      
      // Calculate zoom factor
      const scaleChange = distance / touchState.initialDistance;
      const newScale = Math.max(0.1, Math.min(5, touchState.initialScale * scaleChange));
      
      // Calculate the point to zoom around (center of the two touches)
      const rect = boardRef.current?.getBoundingClientRect();
      if (rect) {
        const centerX = (center.x - rect.left) / scale - offset.x;
        const centerY = (center.y - rect.top) / scale - offset.y;
        
        // Apply zoom
        zoom(newScale);
        
        // Adjust offset to zoom around the center point
        const newOffsetX = centerX - (centerX - offset.x) * (newScale / scale);
        const newOffsetY = centerY - (centerY - offset.y) * (newScale / scale);
        
        // Update offset to maintain zoom center
        pan(newOffsetX - offset.x, newOffsetY - offset.y);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    if (isSelectionMode && selectionBox) {
      endSelectionBox();
    }
    
    // Reset touch state
    setTouchState(prev => ({
      ...prev,
      isPanning: false,
      isZooming: false
    }));
  };

  const handleResetView = () => {
    zoom(0.9);
    pan(-offset.x, -offset.y);
  };

  const handleZoomIn = () => {
    zoom(scale + 0.1);
  };

  const handleZoomOut = () => {
    zoom(scale - 0.1);
  };

  const toggleSidePane = () => {
    setSidePaneOpen(prev => !prev);
  };

  const toggleHelpModal = () => {
    setShowHelpModal(prev => !prev);
  };

  const showBranchCreatedToast = (branchId: string) => {
    setLastCreatedBranchId(branchId);
    setToastMessage('Branch created successfully');
    setToastType('success');
    setShowToastModal(true);
  };

  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const handleShowToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToastModal(true);
  };

  const handleShowMergeToast = (message: string) => {
    setMergeToastMessage(message);
    setShowMergeToast(true);
  };

  const handleUndoBranchCreation = () => {
    if (lastCreatedBranchId) {
      // Delete the branch using the store's deleteNote function
      useWhiteboardStore.getState().deleteNote(lastCreatedBranchId);
      setLastCreatedBranchId(null);
    }
  };

  const handlePersonaSelected = (originalSystemPrompt: string, extractedPersona: string, welcomeMessage?: string, curatedSystemPrompt?: string) => {
    if (personaModalNoteId) {
      const { updateNote } = useWhiteboardStore.getState();
      updateNote(personaModalNoteId, {
        systemPrompt: curatedSystemPrompt || originalSystemPrompt,
        persona: extractedPersona,
        hasCustomSystemPrompt: true,
        needsPersonaSelection: false,
        chatHistory: [{ sender: 'ai', text: welcomeMessage || `Hi, I am JJ, your ${extractedPersona} assistant. How can I help you today?` }],
        // Don't set firstMessageSent here - the first actual message will set it
      });
      
      // Execute parallel async operations for data persistence
      if (user) {
        const effectiveCanvasId = canvasId || 'default';
        const currentState = useWhiteboardStore.getState().state;
        
        // Import and execute parallel save operations
        import('../../utils/parallelSaveUtils').then(({ executeParallelSave }) => {
          executeParallelSave(
            {
              userId: user.uid,
              canvasId: effectiveCanvasId,
              canvasName: currentState.canvasName,
              description: currentState.description,
              tags: currentState.tags
            },
            ['threadStarters', 'totalThreads']
          ).catch((error: unknown) => {
            console.error('Error in parallel save operations:', error);
          });
        }).catch((error: unknown) => {
          console.error('Failed to import parallel save utils:', error);
        });
      }
      
      setShowPersonaModal(false);
      setPersonaModalNoteId(null);
    }
  };

  const handlePersonaModalClose = () => {
    // If user cancels persona selection, delete the note
    if (personaModalNoteId) {
      useWhiteboardStore.getState().deleteNote(personaModalNoteId);
      setShowPersonaModal(false);
      setPersonaModalNoteId(null);
    }
  };

  return (
    <div className={`whiteboard-container ${sidePaneOpen ? 'side-chat-pane-open' : ''}`}>
      <header className="app-header">
        <div className="app-header-content">
          <Link to="/" className="app-header-logo-link">
            <div className="app-header-logo">
              <LogoIcon size={20} color="#10b981" />
              <h1 className="app-header-title">ThreadIt</h1>
            </div>
          </Link>
          <span className="app-header-version">
            v{getAppVersion()}
            {process.env.NODE_ENV === 'development' && (
              <span className="app-header-dev"> (dev)</span>
            )}
          </span>
        </div>
      </header>
      
      <CanvasNameDisplay />
      <SaveStatusIndicator />

      <div
        ref={boardRef}
        className={`whiteboard ${isDragging ? 'grabbing' : ''} ${isSelectionMode ? 'selection-mode' : ''} ${isAnyNoteCreatingBranch ? 'branch-creating' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleBoardClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `scale(${scale}) translate(${offset.x}px, ${offset.y}px)`,
          touchAction: 'none', // Prevent default touch behaviors
        }}
      >
        <div className="grid-background" />

        {/* Render dynamic arrows between parent and branch notes */}
        <svg className="note-arrows" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
          <defs>
            <marker id="arrowhead" markerWidth="16" markerHeight="16" refX="14" refY="8" orient="auto" markerUnits="userSpaceOnUse">
              <path d="M2,2 L14,8 L2,14 L5,8 Z" fill="#333" />
            </marker>
          </defs>
          {notes.filter(n => n.parentId).map(child => {
            const parent = notes.find(n => n.id === child.parentId);
            if (!parent) return null;
            // Calculate start/end points (center of parent to edge of child)
            const startX = parent.position.x + parent.width / 2;
            const startY = parent.position.y + parent.height / 2;
            const endX = child.position.x + child.width / 2;
            const endY = child.position.y + child.height / 2;
            // Calculate direction vector from parent to child
            const dx = endX - startX;
            const dy = endY - startY;
            const length = Math.sqrt(dx * dx + dy * dy);
            // Move endpoint to edge of child note (assuming square notes)
            const noteEdge = Math.min(child.width, child.height) / 2 * scale;
            const ratio = (length - noteEdge) / length;
            const edgeX = startX + dx * ratio;
            const edgeY = startY + dy * ratio;
            // Control points for a smooth curve
            const c1x = startX + dx * 0.3;
            const c1y = startY + dy * 0.1;
            const c2x = edgeX - dx * 0.3;
            const c2y = edgeY - dy * 0.1;
            // Calculate midpoint for text (keep in middle)
            const t = 0.5;
            const bezier = (t: number, p0: number, p1: number, p2: number, p3: number) =>
              Math.pow(1 - t, 3) * p0 + 3 * Math.pow(1 - t, 2) * t * p1 + 3 * (1 - t) * t * t * p2 + Math.pow(t, 3) * p3;
            const midX = bezier(t, startX, c1x, c2x, edgeX);
            const midY = bezier(t, startY, c1y, c2y, edgeY);
            
            // Calculate arrowhead position near branch note (80% along the curve)
            const tArrow = 0.8;
            const arrowX = bezier(tArrow, startX, c1x, c2x, edgeX);
            const arrowY = bezier(tArrow, startY, c1y, c2y, edgeY);
            const bezierDeriv = (t: number, p0: number, p1: number, p2: number, p3: number) =>
              3 * Math.pow(1 - t, 2) * (p1 - p0) + 6 * (1 - t) * t * (p2 - p1) + 3 * t * t * (p3 - p2);
            const dxT = bezierDeriv(tArrow, startX, c1x, c2x, edgeX);
            const dyT = bezierDeriv(tArrow, startY, c1y, c2y, edgeY);
            const angle = Math.atan2(dyT, dxT) * 180 / Math.PI;
            return (
              <g key={child.id + '-branch'}>
                <path
                  d={`M${startX},${startY} C${c1x},${c1y} ${c2x},${c2y} ${edgeX},${edgeY}`}
                  stroke="#333"
                  strokeWidth="2.5"
                  fill="none"
                  opacity="0.85"
                />
                {/* Arrowhead near branch note, pointing to branch note */}
                <g transform={`translate(${arrowX},${arrowY}) rotate(${angle})`}>
                  <polygon points="0,-8 16,0 0,8 4,0" fill="#333" opacity="0.85" />
                </g>
              </g>
            );
          })}
          {/* Render dotted converge lines from branch notes to their merge target if showConverge is true */}
          {notes.filter(n => n.parentId && (n as any).showConverge).map(branch => {
            // Find merge target - use mergeTargetId if available, otherwise find root ancestor
            let target = branch;
            if ((branch as any).mergeTargetId) {
              // Use the specific merge target
              const mergeTarget = notes.find(n => n.id === (branch as any).mergeTargetId);
              if (mergeTarget) {
                target = mergeTarget;
              }
            } else {
              // Fallback: find root ancestor
              while (target.parentId) {
                const parent = notes.find(n => n.id === target.parentId);
                if (!parent) break;
                target = parent;
              }
            }
            
            if (target.id === branch.id) return null;
            
            // Start at 10px above the bottom left corner of branch note, end at edge of target note
            const startX = branch.position.x + 10;
            const startY = branch.position.y + branch.height - 10;
            const endX = target.position.x + target.width / 2;
            const endY = target.position.y + target.height / 2;
            const dx = endX - startX;
            const dy = endY - startY;
            const length = Math.sqrt(dx * dx + dy * dy);
            const targetEdge = Math.min(target.width, target.height) / 2 * scale;
            const ratioEnd = targetEdge / length;
            const edgeStartX = startX; // Start from center of branch note
            const edgeStartY = startY; // Start from center of branch note
            const edgeEndX = endX - dx * ratioEnd;
            const edgeEndY = endY - dy * ratioEnd;
            // Control points for a smooth curve (similar to parent-to-branch)
            const c1x = edgeStartX + (edgeEndX - edgeStartX) * 0.3;
            const c1y = edgeStartY + (edgeEndY - edgeStartY) * 0.1;
            const c2x = edgeEndX - (edgeEndX - edgeStartX) * 0.3;
            const c2y = edgeEndY - (edgeEndY - edgeStartY) * 0.1;
            // Calculate midpoint for text (keep in middle)
            const t = 0.5;
            // Cubic Bezier formula for midpoint
            const bezier = (t: number, p0: number, p1: number, p2: number, p3: number) =>
              Math.pow(1 - t, 3) * p0 + 3 * Math.pow(1 - t, 2) * t * p1 + 3 * (1 - t) * t * t * p2 + Math.pow(t, 3) * p3;
            const midX = bezier(t, edgeStartX, c1x, c2x, edgeEndX);
            const midY = bezier(t, edgeStartY, c1y, c2y, edgeEndY);
            
            // Calculate arrowhead position near target note (90% along the curve)
            const tArrow = 0.9;
            const arrowX = bezier(tArrow, edgeStartX, c1x, c2x, edgeEndX);
            const arrowY = bezier(tArrow, edgeStartY, c1y, c2y, edgeEndY);
            // Derivative for angle
            const bezierDeriv = (t: number, p0: number, p1: number, p2: number, p3: number) =>
              3 * Math.pow(1 - t, 2) * (p1 - p0) + 6 * (1 - t) * t * (p2 - p1) + 3 * t * t * (p3 - p2);
            const dxT = bezierDeriv(tArrow, edgeStartX, c1x, c2x, edgeEndX);
            const dyT = bezierDeriv(tArrow, edgeStartY, c1y, c2y, edgeEndY);
            const angle = Math.atan2(dyT, dxT) * 180 / Math.PI;
            
            // Check if this converge line should glow
            const shouldGlow = (branch as any).showConvergeGlow;
            
            return (
              <g key={branch.id + '-converge'}>
                {/* Glow effect for new converge lines */}
                {shouldGlow && (
                  <defs>
                    <filter id={`glow-${branch.id}`}>
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                )}
                <path
                  d={`M${edgeStartX},${edgeStartY} C${c1x},${c1y} ${c2x},${c2y} ${edgeEndX},${edgeEndY}`}
                  stroke={shouldGlow ? "#1976d2" : "#222"}
                  strokeWidth={shouldGlow ? "4" : "2.5"}
                  strokeDasharray="7,7"
                  fill="none"
                  opacity={shouldGlow ? "0.9" : "0.7"}
                  filter={shouldGlow ? `url(#glow-${branch.id})` : undefined}
                />
                {/* Arrowhead near target note, pointing to target */}
                <g transform={`translate(${arrowX},${arrowY}) rotate(${angle})`}>
                  <polygon 
                    points="0,-8 16,0 0,8 4,0" 
                    fill={shouldGlow ? "#1976d2" : "#222"} 
                    opacity={shouldGlow ? "0.9" : "0.7"}
                  />
                </g>

              </g>
            );
          })}
        </svg>

        {/* Connection Labels - rendered as HTML elements for glassmorphic styling */}
        {notes.filter(n => n.parentId).map(child => {
          const parent = notes.find(n => n.id === child.parentId);
          if (!parent) return null;
          
          // Calculate start/end points (center of parent to edge of child)
          const startX = parent.position.x + parent.width / 2;
          const startY = parent.position.y + parent.height / 2;
          const endX = child.position.x + child.width / 2;
          const endY = child.position.y + child.height / 2;
          
          // Calculate direction vector from parent to child
          const dx = endX - startX;
          const dy = endY - startY;
          const length = Math.sqrt(dx * dx + dy * dy);
          
          // Move endpoint to edge of child note (assuming square notes)
          const noteEdge = Math.min(child.width, child.height) / 2 * scale;
          const ratio = (length - noteEdge) / length;
          const edgeX = startX + dx * ratio;
          const edgeY = startY + dy * ratio;
          
          // Control points for a smooth curve
          const c1x = startX + dx * 0.3;
          const c1y = startY + dy * 0.1;
          const c2x = edgeX - dx * 0.3;
          const c2y = edgeY - dy * 0.1;
          
          // Calculate midpoint for arrow and text
          const t = 0.5;
          const bezier = (t: number, p0: number, p1: number, p2: number, p3: number) =>
            Math.pow(1 - t, 3) * p0 + 3 * Math.pow(1 - t, 2) * t * p1 + 3 * (1 - t) * t * t * p2 + Math.pow(t, 3) * p3;
          const midX = bezier(t, startX, c1x, c2x, edgeX);
          const midY = bezier(t, startY, c1y, c2y, edgeY);
          
          const bezierDeriv = (t: number, p0: number, p1: number, p2: number, p3: number) =>
            3 * Math.pow(1 - t, 2) * (p1 - p0) + 6 * (1 - t) * t * (p2 - p1) + 3 * t * t * (p3 - p2);
          const dxT = bezierDeriv(t, startX, c1x, c2x, edgeX);
          const dyT = bezierDeriv(t, startY, c1y, c2y, edgeY);
          const angle = Math.atan2(dyT, dxT) * 180 / Math.PI;
          
          return (
            <ConnectionLabel
              key={child.id + '-branch-label'}
              label={(child.topic || 'sub-topic').split(' ').slice(0, 3).join(' ')}
              position={{ x: midX, y: midY - 12 }}
              angle={angle}
            />
          );
        })}
        
        {/* Connection Labels for converge lines */}
        {notes.filter(n => n.parentId && (n as any).showConverge).map(branch => {
          // Find merge target - use mergeTargetId if available, otherwise find root ancestor
          let target = branch;
          if ((branch as any).mergeTargetId) {
            const mergeTarget = notes.find(n => n.id === (branch as any).mergeTargetId);
            if (mergeTarget) {
              target = mergeTarget;
            }
          } else {
            while (target.parentId) {
              const parent = notes.find(n => n.id === target.parentId);
              if (!parent) break;
              target = parent;
            }
          }
          
          if (target.id === branch.id) return null;
          
          // Start at 10px above the bottom left corner of branch note, end at edge of target note
          const startX = branch.position.x + 10;
          const startY = branch.position.y + branch.height - 10;
          const endX = target.position.x + target.width / 2;
          const endY = target.position.y + target.height / 2;
          const dx = endX - startX;
          const dy = endY - startY;
          const length = Math.sqrt(dx * dx + dy * dy);
          const branchEdge = Math.min(branch.width, branch.height) / 2 * scale;
          const targetEdge = Math.min(target.width, target.height) / 2 * scale;
          const ratioStart = (length - branchEdge) / length;
          const ratioEnd = targetEdge / length;
          const edgeStartX = startX + dx * ratioStart;
          const edgeStartY = startY + dy * ratioStart;
          const edgeEndX = startX + dx * ratioEnd;
          const edgeEndY = startY + dy * ratioEnd;
          
          // Control points for a smooth curve
          const c1x = edgeStartX + (edgeEndX - edgeStartX) * 0.3;
          const c1y = edgeStartY + (edgeEndY - edgeStartY) * 0.1;
          const c2x = edgeEndX - (edgeEndX - edgeStartX) * 0.3;
          const c2y = edgeEndY - (edgeEndY - edgeStartY) * 0.1;
          
          // Calculate midpoint for arrow and text
          const t = 0.5;
          const bezier = (t: number, p0: number, p1: number, p2: number, p3: number) =>
            Math.pow(1 - t, 3) * p0 + 3 * Math.pow(1 - t, 2) * t * p1 + 3 * (1 - t) * t * t * p2 + Math.pow(t, 3) * p3;
          const midX = bezier(t, edgeStartX, c1x, c2x, edgeEndX);
          const midY = bezier(t, edgeStartY, c1y, c2y, edgeEndY);
          
          const bezierDeriv = (t: number, p0: number, p1: number, p2: number, p3: number) =>
            3 * Math.pow(1 - t, 2) * (p1 - p0) + 6 * (1 - t) * t * (p2 - p1) + 3 * t * t * (p3 - p2);
          const dxT = bezierDeriv(t, edgeStartX, c1x, c2x, edgeEndX);
          const dyT = bezierDeriv(t, edgeStartY, c1y, c2y, edgeEndY);
          const angle = Math.atan2(dyT, dxT) * 180 / Math.PI;
          
          return (
            <ConnectionLabel
              key={branch.id + '-converge-label'}
              label="Summary"
              position={{ x: midX, y: midY - 12 }}
              angle={angle}
            />
          );
        })}

        {notes.map((note) => (
          <StickyNote 
            key={note.id} 
            note={note} 
            onMergeCompleted={handleShowMergeToast}
            isChatOpen={sidePaneOpen && selectedNote?.id === note.id}
          />
        ))}

        {selectionBox && (
          <div
            className="selection-box"
            style={{
              left: Math.min(selectionBox.startX, selectionBox.endX),
              top: Math.min(selectionBox.startY, selectionBox.endY),
              width: Math.abs(selectionBox.endX - selectionBox.startX),
              height: Math.abs(selectionBox.endY - selectionBox.startY),
            }}
          />
        )}
      </div>

      {/* Mobile Tree View */}
      <MobileTreeView
        notes={notes}
        onNoteClick={handleNoteClick}
        onNoteDoubleClick={handleNoteDoubleClick}
        onDelete={handleMobileDelete}
        onColorChange={handleMobileColorChange}
      />

      <Toolbar />

      {/* Onboarding Message for Unsaved Canvas */}
      {needsCanvasNaming(canvasId) && !showLoadingState && (
        <div className="onboarding-message">
          <div className="onboarding-content">
            <h3>Welcome to ThreadIt! 🎉</h3>
            <p>Click the <strong>+</strong> button to create your first note and start brainstorming.</p>
            <p>Need help? Click the <strong>?</strong> button for a quick guide.</p>
          </div>
        </div>
      )}

      {/* Side Chat Pane */}
      <SideChatPane
        isOpen={sidePaneOpen}
        onToggle={toggleSidePane}
        selectedNote={selectedNote}
        onBranchCreated={showBranchCreatedToast}
        onMergeCompleted={handleShowMergeToast}
        isWhiteboardDragging={isDragging}
        onShowToast={handleShowToast}
        isAnyNoteCreatingBranch={isAnyNoteCreatingBranch}
      />

      {/* Help Button */}
      <button
        className={`help-button ${notes.length === 0 ? 'onboarding' : ''}`}
        onClick={toggleHelpModal}
        title={notes.length === 0 ? "Get started - Help Guide" : "Help Guide"}
      >
        ?
      </button>

      {selectedNoteIds.length > 0 && (
        <div className="selection-info">
          {selectedNoteIds.length} note{selectedNoteIds.length > 1 ? 's' : ''} selected
        </div>
      )}

      {/* Toast Notification */}
      {showToastModal && (
        <Toast
          message={toastMessage}
          type={toastType}
          onUndo={handleUndoBranchCreation}
          duration={3000}
          onClose={() => setShowToastModal(false)}
        />
      )}

      {/* Merge Toast Notification */}
      {showMergeToast && (
        <Toast
          message={mergeToastMessage}
          duration={3000}
          onClose={() => setShowMergeToast(false)}
        />
      )}

      {/* Persona Selection Modal */}
      <PersonaSelectionModal
        isOpen={showPersonaModal}
        onClose={handlePersonaModalClose}
        onPersonaSelected={handlePersonaSelected}
        noteId={personaModalNoteId || ''}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        title={alertModalConfig.title}
        message={alertModalConfig.message}
        buttonText={alertModalConfig.buttonText}
        buttonVariant={alertModalConfig.buttonVariant}
      />

      {/* Canvas Name Modal */}
      {showCanvasNameModal && (
        <CanvasNameModal
          isOpen={showCanvasNameModal}
          onClose={handleCanvasNameCancel}
          onSave={handleCanvasNameSave}
          title="Name Your Canvas"
          placeholder="Enter canvas name..."
        />
      )}

      {/* Loading State Overlay */}
      {showLoadingState && (
        <div className="canvas-loading-overlay">
          <div className="canvas-loading-content">
            <div className="loading-spinner"></div>
            <p>Saving canvas and creating your note...</p>
          </div>
        </div>
      )}

      {/* Mobile Toolbar */}
      <MobileToolbar isSideChatOpen={sidePaneOpen} />

      {/* CACHE DEBUG OVERLAY VIEWS - DISABLED */}
      {/* Subscription Cache Debug */}
      {/* <SubscriptionCacheDebug /> */}
      
      {/* Quota Cache Debug */}
      {/* <QuotaCacheDebug /> */}
    </div>
  );
};

export default Whiteboard;